import { useState, type FormEvent } from 'react';
import { useGlossary } from '../contexts/GlossaryContext';
import { submitCareerApplication } from '../lib/api';
import type { CareerApplicationRequest, CareerJobPosting } from '../lib/types';

type Status = 'idle' | 'sending' | 'success' | 'error';

type FormState = {
  fullName: string;
  email: string;
  phone: string;
  currentLocation: string;
  yearsExperience: string;
  availableFrom: string;
  resumeFile: File | null;
  coverLetterFile: File | null;
  portfolioUrl: string;
  motivation: string;
  willingToRelocate: boolean;
  consentToContact: boolean;
};

const FORM_COPY: Record<string, Record<string, string>> = {
  hi: {
    'careers.form.title': 'इस पद हेतु आवेदन करें',
    'careers.form.subtitle': 'केवल वही जानकारी भरें जो भूमिका के मूल्यांकन और आपसे संपर्क के लिए आवश्यक है।',
    'careers.form.field.fullName': 'पूरा नाम',
    'careers.form.field.email': 'ईमेल',
    'careers.form.field.phone': 'मोबाइल नम्बर',
    'careers.form.field.currentLocation': 'वर्तमान स्थान',
    'careers.form.field.yearsExperience': 'प्रासंगिक अनुभव (वर्ष)',
    'careers.form.field.availableFrom': 'आप कब से उपलब्ध हैं?',
    'careers.form.field.resumeFile': 'रिज्यूमे / CV फ़ाइल',
    'careers.form.field.coverLetterFile': 'कवर लेटर फ़ाइल',
    'careers.form.field.portfolioUrl': 'LinkedIn / पोर्टफोलियो लिंक',
    'careers.form.field.motivation': 'यह भूमिका आपके लिए क्यों उपयुक्त है?',
    'careers.form.field.relocate': 'मैं आवश्यकता होने पर धर्मशाला / भूमिका-स्थान पर कार्य करने या स्थानांतरित होने के लिए तैयार हूँ।',
    'careers.form.field.consent': 'मैं इस आवेदन के संबंध में ईमेल/फ़ोन द्वारा संपर्क किए जाने के लिए सहमत हूँ।',
    'careers.form.placeholder.fullName': 'जैसा आपके आधिकारिक अभिलेखों में है',
    'careers.form.placeholder.email': 'you@example.com',
    'careers.form.placeholder.phone': '+91 98765 43210',
    'careers.form.placeholder.currentLocation': 'शहर, राज्य, देश',
    'careers.form.placeholder.availableFrom': 'उदाहरण: तत्काल, 30 दिन, 1 जून 2026',
    'careers.form.placeholder.portfolioUrl': 'https://linkedin.com/in/...',
    'careers.form.placeholder.motivation': 'अपने अनुभव, संबंधित कौशल, और इस पद में रुचि के बारे में संक्षेप में लिखें…',
    'careers.form.optional': 'वैकल्पिक',
    'careers.form.fileHelp.resume': 'PDF, DOC, DOCX, या TXT',
    'careers.form.fileHelp.coverLetter': 'यदि उपलब्ध हो तो PDF/DOC में अपलोड करें',
    'careers.form.submit': 'आवेदन भेजें',
    'careers.form.submitting': 'आवेदन भेजा जा रहा है…',
    'careers.form.cancel': 'रद्द करें',
    'careers.form.success.title': 'आवेदन प्राप्त हुआ',
    'careers.form.success.body': 'हमने {email} के साथ आपका आवेदन दर्ज कर लिया है।',
    'careers.form.success.reference': 'आवेदन सन्दर्भ',
    'careers.form.success.note': 'यदि भूमिका के लिए चयन प्रक्रिया आगे बढ़ती है, तो टीम आपसे इसी विवरण पर संपर्क करेगी।',
    'careers.form.error.generic': 'आवेदन भेजा नहीं जा सका। कृपया पुनः प्रयास करें।',
    'careers.form.error.fullName': 'पूरा नाम आवश्यक है।',
    'careers.form.error.email': 'वैध ईमेल आवश्यक है।',
    'careers.form.error.phone': 'वैध मोबाइल नम्बर आवश्यक है।',
    'careers.form.error.currentLocation': 'वर्तमान स्थान आवश्यक है।',
    'careers.form.error.yearsExperience': 'प्रासंगिक अनुभव 0 या उससे अधिक होना चाहिए।',
    'careers.form.error.availableFrom': 'उपलब्धता जानकारी आवश्यक है।',
    'careers.form.error.resumeFile': 'रिज्यूमे फ़ाइल आवश्यक है।',
    'careers.form.error.portfolioUrl': 'यदि दिया जाए, तो वैध लिंक होना चाहिए।',
    'careers.form.error.motivation': 'कृपया कम से कम 40 अक्षरों में भूमिका-उपयुक्तता लिखें।',
    'careers.form.error.consent': 'संपर्क सहमति आवश्यक है।',
  },
  en: {
    'careers.form.title': 'Apply for this role',
    'careers.form.subtitle': 'Share only the information needed to evaluate your fit for this role and contact you.',
    'careers.form.field.fullName': 'Full name',
    'careers.form.field.email': 'Email',
    'careers.form.field.phone': 'Phone number',
    'careers.form.field.currentLocation': 'Current location',
    'careers.form.field.yearsExperience': 'Relevant experience (years)',
    'careers.form.field.availableFrom': 'When can you start?',
    'careers.form.field.resumeFile': 'Resume / CV file',
    'careers.form.field.coverLetterFile': 'Cover letter file',
    'careers.form.field.portfolioUrl': 'LinkedIn / portfolio link',
    'careers.form.field.motivation': 'Why are you a fit for this role?',
    'careers.form.field.relocate': 'I am willing to work from or relocate to Dharamshala / the role location if needed.',
    'careers.form.field.consent': 'I consent to being contacted by email or phone about this application.',
    'careers.form.placeholder.fullName': 'As shown on your official records',
    'careers.form.placeholder.email': 'you@example.com',
    'careers.form.placeholder.phone': '+91 98765 43210',
    'careers.form.placeholder.currentLocation': 'City, state, country',
    'careers.form.placeholder.availableFrom': 'Example: Immediate, 30 days, 1 June 2026',
    'careers.form.placeholder.portfolioUrl': 'https://linkedin.com/in/...',
    'careers.form.placeholder.motivation': 'Briefly describe your experience, relevant skills, and interest in the role…',
    'careers.form.optional': 'Optional',
    'careers.form.fileHelp.resume': 'PDF, DOC, DOCX, or TXT',
    'careers.form.fileHelp.coverLetter': 'Upload as PDF/DOC if available',
    'careers.form.submit': 'Send application',
    'careers.form.submitting': 'Sending application…',
    'careers.form.cancel': 'Cancel',
    'careers.form.success.title': 'Application received',
    'careers.form.success.body': 'We have recorded your application with {email}.',
    'careers.form.success.reference': 'Application reference',
    'careers.form.success.note': 'If the role moves forward, the team will contact you using these details.',
    'careers.form.error.generic': 'The application could not be submitted. Please try again.',
    'careers.form.error.fullName': 'Full name is required.',
    'careers.form.error.email': 'A valid email is required.',
    'careers.form.error.phone': 'A valid phone number is required.',
    'careers.form.error.currentLocation': 'Current location is required.',
    'careers.form.error.yearsExperience': 'Relevant experience must be 0 or greater.',
    'careers.form.error.availableFrom': 'Availability details are required.',
    'careers.form.error.resumeFile': 'A resume file is required.',
    'careers.form.error.portfolioUrl': 'If provided, the portfolio link must be valid.',
    'careers.form.error.motivation': 'Please describe your fit in at least 40 characters.',
    'careers.form.error.consent': 'Contact consent is required.',
  },
  sa: {
    'careers.form.title': 'अस्य पदस्य आवेदनं कुरुत',
    'careers.form.subtitle': 'भूमिकायाः मूल्याङ्कनाय तथा भवता सह सम्पर्काय यावत् आवश्यकं तावत् एव विवरणं दत्तम्।',
    'careers.form.field.fullName': 'पूर्णनाम',
    'careers.form.field.email': 'ईमेल',
    'careers.form.field.phone': 'दूरवाणी संख्या',
    'careers.form.field.currentLocation': 'वर्तमान-स्थानम्',
    'careers.form.field.yearsExperience': 'संबद्धानुभवः (वर्षेषु)',
    'careers.form.field.availableFrom': 'भवान् कदा आरभितुं शक्नोति?',
    'careers.form.field.resumeFile': 'रिज्यूमे / CV-फ़ाइलम्',
    'careers.form.field.coverLetterFile': 'कवर-लेटर फ़ाइलम्',
    'careers.form.field.portfolioUrl': 'LinkedIn / कार्यसञ्चय-सूत्रम्',
    'careers.form.field.motivation': 'अस्य पदस्य कृते भवान् कथं योग्यः?',
    'careers.form.field.relocate': 'आवश्यकतायाम् अहं धर्मशालायां / कार्य-स्थले कार्यं कर्तुं वा स्थानान्तरं कर्तुं वा सिद्धः अस्मि।',
    'careers.form.field.consent': 'अस्य आवेदनस्य विषये ईमेल-दूरवाणी-माध्यमेन सम्पर्कस्य अनुमतिं ददामि।',
    'careers.form.placeholder.fullName': 'यथा आधिकारिक-अभिलेखे अस्ति',
    'careers.form.placeholder.email': 'you@example.com',
    'careers.form.placeholder.phone': '+91 98765 43210',
    'careers.form.placeholder.currentLocation': 'नगरम्, राज्यं, देशः',
    'careers.form.placeholder.availableFrom': 'उदाहरणम्: तत्क्षणात्, 30 दिवसानन्तरम्, 1 June 2026',
    'careers.form.placeholder.portfolioUrl': 'https://linkedin.com/in/...',
    'careers.form.placeholder.motivation': 'स्वानुभवं, कौशलानि, अस्मिन् पदे रुचिं च संक्षेपेण लिखत…',
    'careers.form.optional': 'वैकल्पिकम्',
    'careers.form.fileHelp.resume': 'PDF, DOC, DOCX, अथवा TXT',
    'careers.form.fileHelp.coverLetter': 'यदि उपलब्धम् अस्ति तर्हि PDF/DOC अपलोडयतु',
    'careers.form.submit': 'आवेदनं प्रेषयतु',
    'careers.form.submitting': 'आवेदनं प्रेष्यते…',
    'careers.form.cancel': 'निरस्तम्',
    'careers.form.success.title': 'आवेदनं प्राप्तम्',
    'careers.form.success.body': '{email} सहितं भवतः आवेदनं अभिलेखितम्।',
    'careers.form.success.reference': 'आवेदन-सन्दर्भः',
    'careers.form.success.note': 'यदि अस्य पदस्य प्रक्रिया अग्रे गच्छति, दलं एतैः विवरणैः भवता सह सम्पर्कं करिष्यति।',
    'careers.form.error.generic': 'आवेदनं प्रेषयितुं न शक्यते। कृपया पुनः प्रयतध्वम्।',
    'careers.form.error.fullName': 'पूर्णनाम आवश्यकम्।',
    'careers.form.error.email': 'वैध ईमेल आवश्यकः।',
    'careers.form.error.phone': 'वैध दूरवाणी संख्या आवश्यकाः।',
    'careers.form.error.currentLocation': 'वर्तमान-स्थानम् आवश्यकम्।',
    'careers.form.error.yearsExperience': 'संबद्धानुभवः 0 अथवा ततः अधिकः भवेत्।',
    'careers.form.error.availableFrom': 'उपलब्धता-विवरणम् आवश्यकम्।',
    'careers.form.error.resumeFile': 'रिज्यूमे-फ़ाइलम् आवश्यकम्।',
    'careers.form.error.portfolioUrl': 'यदि दत्तं भवेत्, तर्हि वैध-सूत्रं भवेत्।',
    'careers.form.error.motivation': 'कृपया न्यूनातिन्यूनं 40 अक्षरैः स्वयोग्यतां लिखत।',
    'careers.form.error.consent': 'सम्पर्क-सहमति आवश्यकाः।',
  },
};

const EMPTY_FORM: FormState = {
  fullName: '',
  email: '',
  phone: '',
  currentLocation: '',
  yearsExperience: '',
  availableFrom: '',
  resumeFile: null,
  coverLetterFile: null,
  portfolioUrl: '',
  motivation: '',
  willingToRelocate: false,
  consentToContact: false,
};

function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

export default function CareerApplicationForm({
  job,
  onCancel,
}: {
  job: CareerJobPosting;
  onCancel: () => void;
}) {
  const { t, locale } = useGlossary();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [applicationId, setApplicationId] = useState('');

  const localized = (key: string) => {
    const translated = t(key);
    if (translated !== key) return translated;
    return FORM_COPY[locale]?.[key] ?? FORM_COPY.en[key] ?? key;
  };

  const update = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};

    if (!form.fullName.trim()) nextErrors.fullName = localized('careers.form.error.fullName');
    if (!form.email.trim() || !form.email.includes('@')) nextErrors.email = localized('careers.form.error.email');

    const phoneDigits = form.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) nextErrors.phone = localized('careers.form.error.phone');

    if (!form.currentLocation.trim()) nextErrors.currentLocation = localized('careers.form.error.currentLocation');

    const years = Number(form.yearsExperience);
    if (!Number.isFinite(years) || years < 0) {
      nextErrors.yearsExperience = localized('careers.form.error.yearsExperience');
    }

    if (!form.availableFrom.trim()) nextErrors.availableFrom = localized('careers.form.error.availableFrom');
    if (!form.resumeFile) nextErrors.resumeFile = localized('careers.form.error.resumeFile');
    if (form.portfolioUrl.trim() && !isHttpUrl(form.portfolioUrl.trim())) {
      nextErrors.portfolioUrl = localized('careers.form.error.portfolioUrl');
    }
    if (form.motivation.trim().length < 40) nextErrors.motivation = localized('careers.form.error.motivation');
    if (!form.consentToContact) nextErrors.consentToContact = localized('careers.form.error.consent');

    return nextErrors;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const resumeFile = form.resumeFile;
    if (!resumeFile) {
      setErrors({ resumeFile: localized('careers.form.error.resumeFile') });
      return;
    }

    setStatus('sending');
    setErrorMessage('');

    const payload: CareerApplicationRequest = {
      jobId: job.id,
      locale,
      fullName: form.fullName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      currentLocation: form.currentLocation.trim(),
      yearsExperience: Number(form.yearsExperience),
      availableFrom: form.availableFrom.trim(),
      resumeFile,
      coverLetterFile: form.coverLetterFile,
      portfolioUrl: form.portfolioUrl.trim() || undefined,
      motivation: form.motivation.trim(),
      willingToRelocate: form.willingToRelocate,
      consentToContact: form.consentToContact,
      referrer: document.referrer || undefined,
    };

    try {
      const result = await submitCareerApplication(payload);
      setApplicationId(result.application_id);
      setStatus('success');
    } catch (error) {
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : localized('careers.form.error.generic'));
    }
  };

  if (status === 'success') {
    return (
      <div className="career-application-success" role="status">
        <div className="career-application-success-icon">✅</div>
        <h4>{localized('careers.form.success.title')}</h4>
        <p>{localized('careers.form.success.body').replace('{email}', form.email.trim())}</p>
        <p className="career-application-reference">
          <strong>{localized('careers.form.success.reference')}:</strong> <code>{applicationId}</code>
        </p>
        <p>{localized('careers.form.success.note')}</p>
      </div>
    );
  }

  return (
    <div className="career-application-form-shell">
      <div className="career-application-heading">
        <h4>{localized('careers.form.title')}</h4>
        <p>{localized('careers.form.subtitle')}</p>
      </div>

      <div className="career-application-role-summary">
        <span>{job.title}</span>
        <span>{job.location}</span>
        <span>{job.type}</span>
      </div>

      <form className="career-application-form" onSubmit={handleSubmit} noValidate>
        <div className="career-form-grid">
          <label className="career-form-field">
            <span>{localized('careers.form.field.fullName')}</span>
            <input
              type="text"
              value={form.fullName}
              autoComplete="name"
              placeholder={localized('careers.form.placeholder.fullName')}
              onChange={(event) => update('fullName', event.target.value)}
            />
            {errors.fullName && <span className="career-form-error">{errors.fullName}</span>}
          </label>

          <label className="career-form-field">
            <span>{localized('careers.form.field.email')}</span>
            <input
              type="email"
              value={form.email}
              autoComplete="email"
              placeholder={localized('careers.form.placeholder.email')}
              onChange={(event) => update('email', event.target.value)}
            />
            {errors.email && <span className="career-form-error">{errors.email}</span>}
          </label>

          <label className="career-form-field">
            <span>{localized('careers.form.field.phone')}</span>
            <input
              type="tel"
              value={form.phone}
              autoComplete="tel"
              placeholder={localized('careers.form.placeholder.phone')}
              onChange={(event) => update('phone', event.target.value)}
            />
            {errors.phone && <span className="career-form-error">{errors.phone}</span>}
          </label>

          <label className="career-form-field">
            <span>{localized('careers.form.field.currentLocation')}</span>
            <input
              type="text"
              value={form.currentLocation}
              placeholder={localized('careers.form.placeholder.currentLocation')}
              onChange={(event) => update('currentLocation', event.target.value)}
            />
            {errors.currentLocation && <span className="career-form-error">{errors.currentLocation}</span>}
          </label>

          <label className="career-form-field">
            <span>{localized('careers.form.field.yearsExperience')}</span>
            <input
              type="number"
              min="0"
              step="0.5"
              value={form.yearsExperience}
              onChange={(event) => update('yearsExperience', event.target.value)}
            />
            {errors.yearsExperience && <span className="career-form-error">{errors.yearsExperience}</span>}
          </label>

          <label className="career-form-field">
            <span>{localized('careers.form.field.availableFrom')}</span>
            <input
              type="text"
              value={form.availableFrom}
              placeholder={localized('careers.form.placeholder.availableFrom')}
              onChange={(event) => update('availableFrom', event.target.value)}
            />
            {errors.availableFrom && <span className="career-form-error">{errors.availableFrom}</span>}
          </label>

          <label className="career-form-field">
            <span>{localized('careers.form.field.resumeFile')}</span>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.txt,.rtf"
              onChange={(event) => update('resumeFile', event.target.files?.[0] ?? null)}
            />
            <span className="career-form-file-help">{localized('careers.form.fileHelp.resume')}</span>
            {form.resumeFile && <span className="career-form-file-name">{form.resumeFile.name}</span>}
            {errors.resumeFile && <span className="career-form-error">{errors.resumeFile}</span>}
          </label>

          <label className="career-form-field">
            <span>
              {localized('careers.form.field.coverLetterFile')}{' '}
              <em>({localized('careers.form.optional')})</em>
            </span>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.txt,.rtf"
              onChange={(event) => update('coverLetterFile', event.target.files?.[0] ?? null)}
            />
            <span className="career-form-file-help">{localized('careers.form.fileHelp.coverLetter')}</span>
            {form.coverLetterFile && <span className="career-form-file-name">{form.coverLetterFile.name}</span>}
          </label>

          <label className="career-form-field">
            <span>
              {localized('careers.form.field.portfolioUrl')}{' '}
              <em>({localized('careers.form.optional')})</em>
            </span>
            <input
              type="url"
              value={form.portfolioUrl}
              placeholder={localized('careers.form.placeholder.portfolioUrl')}
              onChange={(event) => update('portfolioUrl', event.target.value)}
            />
            {errors.portfolioUrl && <span className="career-form-error">{errors.portfolioUrl}</span>}
          </label>
        </div>

        <label className="career-form-field">
          <span>{localized('careers.form.field.motivation')}</span>
          <textarea
            rows={6}
            value={form.motivation}
            placeholder={localized('careers.form.placeholder.motivation')}
            onChange={(event) => update('motivation', event.target.value)}
          />
          {errors.motivation && <span className="career-form-error">{errors.motivation}</span>}
        </label>

        <label className="career-form-checkbox">
          <input
            type="checkbox"
            checked={form.willingToRelocate}
            onChange={(event) => update('willingToRelocate', event.target.checked)}
          />
          <span>{localized('careers.form.field.relocate')}</span>
        </label>

        <label className="career-form-checkbox">
          <input
            type="checkbox"
            checked={form.consentToContact}
            onChange={(event) => update('consentToContact', event.target.checked)}
          />
          <span>{localized('careers.form.field.consent')}</span>
        </label>
        {errors.consentToContact && <span className="career-form-error career-form-error-inline">{errors.consentToContact}</span>}

        {status === 'error' && (
          <div className="career-form-error career-form-error-global">
            {errorMessage || localized('careers.form.error.generic')}
          </div>
        )}

        <div className="career-form-actions">
          <button type="submit" className="apply-button" disabled={status === 'sending'}>
            {status === 'sending' ? localized('careers.form.submitting') : localized('careers.form.submit')}
          </button>
          <button type="button" className="career-form-cancel" onClick={onCancel}>
            {localized('careers.form.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}
